import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { BottomSheet } from './primitives';
import { Colors } from './theme';

/**
 * The controls a phone screen fills a form with.
 *
 * Lifted out of the supervisor's job card editor so the admin's screens ask
 * their questions with the same controls: a 48px target under its label, a
 * select that opens as a sheet at the thumb rather than a native dropdown at
 * the top, and Yes/No as two buttons instead of a checkbox nobody can hit.
 */

export const fieldName = (field) => field.name || field.field_name;
export const fieldType = (field) => field.type || field.field_type || 'text';

/** Options come back as plain strings or as {value,label} — read either. */
const asOption = (o) => (typeof o === 'string'
  ? { value: o, label: o }
  : { value: o.value ?? o.label ?? String(o), label: o.label ?? o.value ?? String(o) });

export const fieldOptions = (field) => (field.options || []).map(asOption);

export const inputStyle = {
  backgroundColor: Colors.background.primary,
  borderColor: Colors.border.default,
  color: Colors.text.primary,
};

export const INPUT_CLASS = 'h-12 w-full rounded-[10px] border px-3 text-[14px] outline-none';

/** A label above its control, the way every phone form asks a question. */
export function Field({ label, hint, action, children, className = '' }) {
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{label}</p>
        {action}
      </div>
      {!!hint && <p className="-mt-1 mb-1.5 text-[11px]" style={{ color: Colors.text.tertiary }}>{hint}</p>}
      {children}
    </div>
  );
}

/** A picker that opens at the thumb rather than a native dropdown at the top. */
export function SelectField({ label, value, options, onChange, name, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const items = (options || []).map(asOption);
  const current = items.find((o) => o.value === value);
  return (
    <>
      <button
        type="button"
        data-testid={`field-${name}`}
        onClick={() => setOpen(true)}
        className="flex h-12 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-left"
        style={{
          backgroundColor: Colors.background.primary,
          borderColor: Colors.border.default,
        }}
      >
        <span
          className="truncate text-[14px]"
          style={{ color: value ? Colors.text.primary : Colors.text.tertiary }}
        >
          {current ? current.label : (value || placeholder)}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0" style={{ color: Colors.text.secondary }} />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={label} testID={`picker-${name}`}>
        <div className="max-h-[19rem] overflow-y-auto">
          {items.length === 0 && (
            <p className="py-4 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
              This field has no options configured.
            </p>
          )}
          {items.map((option) => {
            const on = value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`option-${name}-${option.value}`}
                onClick={() => { onChange(option.value); setOpen(false); }}
                className="flex w-full items-center justify-between gap-3 border-b px-1 py-3.5 text-left last:border-b-0"
                style={{ borderColor: Colors.border.subtle }}
              >
                <span
                  className="truncate text-[15px]"
                  style={{
                    color: on ? Colors.accent.DEFAULT : Colors.text.primary,
                    fontWeight: on ? 700 : 400,
                  }}
                >
                  {option.label}
                </span>
                {on && <Check className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.accent.DEFAULT }} />}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}

/** Yes / No, as two buttons — a checkbox is a small target and says less. */
export function BooleanField({ value, onChange, name }) {
  return (
    <div className="flex gap-2" data-testid={`field-${name}`}>
      {['Yes', 'No'].map((option) => {
        const on = value === option;
        return (
          <button
            key={option}
            type="button"
            data-testid={`bool-${name}-${option}`}
            aria-pressed={on}
            onClick={() => onChange(option)}
            className="h-12 flex-1 rounded-[10px] border text-[14px]"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
              fontWeight: on ? 600 : 500,
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

/** Several of a list, as chips that toggle. */
export function MultiField({ value, options, onChange, name }) {
  const chosen = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={`field-${name}`}>
      {(options || []).map(asOption).filter((o) => o.value !== 'None').map((option) => {
        const on = chosen.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            data-testid={`multi-${name}-${option.value}`}
            aria-pressed={on}
            onClick={() => onChange(on
              ? chosen.filter((v) => v !== option.value)
              : [...chosen.filter((v) => v !== 'None'), option.value])}
            className="h-10 rounded-[10px] border px-3 text-[13px]"
            style={{
              backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
              borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
              color: on ? Colors.text.inverse : Colors.text.secondary,
              fontWeight: on ? 600 : 500,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** One field of a product config, as the control its type asks for. */
export function FieldRenderer({ field, value, onChange }) {
  const type = fieldType(field);
  const name = fieldName(field);
  const label = (field.label || name || '').replace(/_/g, ' ');

  if (type === 'checkbox_group') {
    return <MultiField name={name} value={value} options={fieldOptions(field)} onChange={onChange} />;
  }

  const text = value === undefined || value === null ? '' : String(value);

  if (type === 'select' || type === 'dropdown') {
    return (
      <SelectField
        name={name}
        label={label}
        value={text}
        options={fieldOptions(field)}
        onChange={onChange}
      />
    );
  }
  if (type === 'boolean' || type === 'checkbox' || type === 'toggle') {
    return <BooleanField name={name} value={text || 'No'} onChange={onChange} />;
  }
  if (type === 'textarea' || type === 'multiline') {
    return (
      <textarea
        data-testid={`field-${name}`}
        className="min-h-[80px] w-full resize-y rounded-[10px] border px-3 py-2.5 text-[14px] outline-none"
        style={inputStyle}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value…"
        aria-label={label}
        rows={3}
      />
    );
  }
  if (type === 'datetime') {
    return (
      <input
        data-testid={`field-${name}`}
        className={INPUT_CLASS}
        style={inputStyle}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        type="datetime-local"
        aria-label={label}
      />
    );
  }
  return (
    <input
      data-testid={`field-${name}`}
      className={INPUT_CLASS}
      style={inputStyle}
      value={text}
      onChange={(e) => onChange(e.target.value)}
      type={type === 'date' ? 'date' : 'text'}
      inputMode={type === 'number' ? 'decimal' : undefined}
      placeholder={type === 'number' ? '0' : 'Enter value…'}
      aria-label={label}
    />
  );
}
